/**
 * lanternStyle.ts — the Lantern slot (lane BK, backlog B26).
 *
 * A small light the ghost carries: it bobs beside the shoulder, behind the
 * body, tinted and sized by the equipped Lantern item. The slot is stored as
 * item slot `lantern` ↔ schema slot `charm` (src/shared/equipmentSlots.ts), an
 * additive `equipment` field: old saves load it empty, so there is no
 * migration. Each Lantern item authors its look under `[<item>.lantern]` in
 * content TOML (compile-content validates it), so a new lantern is data only.
 *
 * An empty slot draws no light. Remote clients receive every equipped item id
 * (the `equipment` schema subtree → `player.equipmentVisuals`), so the same
 * lookup lights the local player's lantern and everyone else's.
 */
import type { ResolvedPlayerEquipmentVisuals, EquipmentVisualResolution } from './equipmentVisuals';
import { ITEM_DATABASE } from './content/generated/items.gen';

/** The internal item slot a lantern is stored in (saves, wire, content). */
export const LANTERN_ITEM_SLOT = 'lantern' as const;
/** The schema/body slot a lantern is stored in (`player.equipment.charm`). */
export const LANTERN_SCHEMA_SLOT = 'charm' as const;

/**
 * One lantern look. Authored in TOML as `color`/`glow`/`size`/`bob`/`speed`.
 * Cheap by construction: the renderer draws two glow sprites once and only
 * moves and fades them.
 */
export interface ItemLanternStyle {
  /** Light tint, `#rgb` or `#rrggbb`. */
  color: string;
  /** Peak alpha of the halo, 0.2–1. */
  glow: number;
  /** Halo diameter in px, 16–48; the bright core is about a third of it. */
  size: number;
  /** Bob amplitude in px, 0–6 (0 hangs still). */
  bob: number;
  /** Bob cycles per second, 0.2–2. */
  speed: number;
}

/** The bounds compile-content enforces (scripts/content-schemas.ts mirrors them). */
export const LANTERN_STYLE_LIMITS = {
  glow: [0.2, 1],
  size: [16, 48],
  bob: [0, 6],
  speed: [0.2, 2],
} as const;

const HEX = /^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
const within = (v: unknown, [lo, hi]: readonly [number, number]) =>
  typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;

/** Whether a lantern style is renderable (the same contract the compiler enforces). */
export function isValidLanternStyle(s: unknown): s is ItemLanternStyle {
  if (!s || typeof s !== 'object') return false;
  const t = s as Record<string, unknown>;
  return typeof t.color === 'string' && HEX.test(t.color)
    && within(t.glow, LANTERN_STYLE_LIMITS.glow)
    && within(t.size, LANTERN_STYLE_LIMITS.size)
    && within(t.bob, LANTERN_STYLE_LIMITS.bob)
    && within(t.speed, LANTERN_STYLE_LIMITS.speed);
}

/** The authored lantern of an item, or null (unknown id, not a Lantern item, corrupt style). */
export function lanternStyleForItemId(itemId: string | null | undefined): ItemLanternStyle | null {
  if (!itemId) return null;
  const item = ITEM_DATABASE[itemId];
  if (!item || item.equipmentSlot !== LANTERN_ITEM_SLOT) return null;
  return isValidLanternStyle(item.lantern) ? item.lantern : null;
}

function resolutionItemId(r: EquipmentVisualResolution | undefined): string | null {
  if (!r || r.kind === 'empty') return null;
  return r.kind === 'resolved' ? r.descriptor.itemId : r.itemId;
}

/** The item id in a player's Lantern slot, from the equipment projection every client has. */
export function equippedLanternItemId(visuals: ResolvedPlayerEquipmentVisuals | undefined | null): string | null {
  return resolutionItemId(visuals?.[LANTERN_SCHEMA_SLOT]);
}

/** The lantern a player's ghost should carry, or null — an empty slot draws no light. */
export function lanternStyleForEquipment(visuals: ResolvedPlayerEquipmentVisuals | undefined | null): ItemLanternStyle | null {
  return lanternStyleForItemId(equippedLanternItemId(visuals));
}

/** The internal item slot a flame is stored in, and its schema/body slot (`player.equipment.halo`). */
export const FLAME_ITEM_SLOT = 'flame' as const;
export const FLAME_SCHEMA_SLOT = 'halo' as const;

/**
 * The Flame slot (DJ 2026-10-23: Mallow's Rare Wick, "like the lanternglow
 * wisp"): a small flame floating above the head. It is the Lantern light in
 * crown placement (GhostLanternFx), authored the same way under
 * `[<item>.flame]`, so a new coloured flame is data only.
 */
export function flameStyleForItemId(itemId: string | null | undefined): ItemLanternStyle | null {
  if (!itemId) return null;
  const item = ITEM_DATABASE[itemId];
  if (!item || item.equipmentSlot !== FLAME_ITEM_SLOT) return null;
  return isValidLanternStyle(item.flame) ? item.flame : null;
}

/** The flame a player's ghost floats above its head, or null — an empty slot draws none. */
export function flameStyleForEquipment(visuals: ResolvedPlayerEquipmentVisuals | undefined | null): ItemLanternStyle | null {
  return flameStyleForItemId(resolutionItemId(visuals?.[FLAME_SCHEMA_SLOT]));
}
